import React, { useState, useRef, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Badge } from '../ui/badge';
import {
  LayoutDashboard,
  Kanban,
  Users,
  ListTodo,
  Activity,
  Phone,
  PhoneCall,
  Video,
  BarChart3,
  FileText,
  Settings,
  User,
  LogOut,
  Menu,
  X,
  Bell,
  ChevronDown,
  Building2,
  Award,
  Upload,
  RefreshCw,
  Database,
} from 'lucide-react';
import DailyReportDialog from '../dialogs/DailyReportDialog';
import NotificationBell from '../NotificationBell';

const DashboardLayout = () => {
  const { user, logout, isManager } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showDailyReport, setShowDailyReport] = useState(false);
  const navScrollRef = useRef(null);

  const handleLogout = () => {
    if (user?.role === 'Executive') {
      setShowDailyReport(true);
    } else {
      logout();
      navigate('/login');
    }
  };

  const handleDailyReportSubmit = () => {
    setShowDailyReport(false);
    logout();
    navigate('/login');
  };

  // Scroll active item into view when route changes
  useEffect(() => {
    if (navScrollRef.current) {
      const activeItem = navScrollRef.current.querySelector('.nav-active');
      if (activeItem) {
        activeItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [location.pathname]);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (sidebarOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // R2G users have a completely different navigation
  const isR2GUser = user?.role === 'R2G';

  const navItems = isR2GUser ? [
    { path: '/r2g', label: 'Duplicate Check', icon: LayoutDashboard, roles: ['R2G'], primary: true },
  ] : [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'], primary: true },
    { path: '/leads-board', label: 'Lead Board', icon: Kanban, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'], primary: true },
    { path: '/quick-call', label: 'Quick Call', icon: PhoneCall, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'], primary: true },
    { path: '/lms-meet', label: 'LMS Virtual Meet', icon: Video, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'], primary: true },
    { path: '/all-leads', label: 'All Leads', icon: Users, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/import', label: 'Import Leads', icon: Upload, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/sheet-monitor', label: 'Google Sheets Engine', icon: Database, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/franchise-outlets', label: 'Franchise Outlets', icon: Building2, roles: ['Super Admin', 'CEO', 'Manager'] },
    // Reminders / Follow-ups / Activity Feed hidden for Super Admin (Jul 2025 product decision)
    { path: '/reminders', label: 'Reminders', icon: Bell, roles: ['CEO', 'Manager', 'Executive'] },
    { path: '/tasks', label: 'Follow-ups', icon: ListTodo, roles: ['CEO', 'Manager', 'Executive'] },
    { path: '/activity-feed', label: 'Activity Feed', icon: Activity, roles: ['CEO', 'Manager'] },
    { path: '/call-logs', label: 'Call Logs', icon: Phone, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'] },
    { path: '/call-activity', label: 'Call Activity', icon: PhoneCall, roles: ['Super Admin', 'CEO', 'Manager', 'Executive'] },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/loi-analysis', label: 'LOI Analysis', icon: FileText, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/executive-report', label: 'Exec Report', icon: Award, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/teams', label: 'Teams', icon: Building2, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/reports', label: 'Reports', icon: FileText, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/daily-reports', label: 'Daily Reports', icon: FileText, roles: ['Super Admin', 'CEO', 'Manager'] },
    { path: '/settings', label: 'Settings', icon: Settings, roles: ['Super Admin', 'CEO', 'Manager'] },
  ];

  const filteredNavItems = navItems.filter((item) =>
    item.roles.includes(user?.role || '')
  );

  const getInitials = (name) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const primaryItems = filteredNavItems.filter((item) => item.primary);
  const secondaryItems = filteredNavItems.filter((item) => !item.primary);

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-50 px-4 py-3 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(true)}
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-primary" />
          <span className="font-bold font-['Manrope']">CRM</span>
        </div>
        <NotificationBell />
      </header>

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50 w-64 bg-card border-r
          transform transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          flex flex-col h-screen
        `}
        style={{ height: '100vh', maxHeight: '100vh' }}
      >
        {/* Fixed Sidebar Header - Logo */}
        <div className="flex-shrink-0 h-16 flex items-center justify-between px-4 border-b bg-card">
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_leadmaster-51/artifacts/eo8fbhnh_servalllogo.jpeg"
              alt="Servall"
              className="h-10 w-10 rounded object-contain"
            />
            <span className="text-xl font-bold font-['Manrope'] text-primary">Servall</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Scrollable Navigation Area */}
        <nav
          ref={navScrollRef}
          className="
            flex-1 overflow-y-auto overflow-x-hidden
            scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent
            hover:scrollbar-thumb-gray-400
            overscroll-contain
          "
          style={{
            WebkitOverflowScrolling: 'touch', // Smooth momentum scrolling on iOS
            scrollBehavior: 'smooth',
          }}
          onWheel={(e) => {
            // Prevent scroll propagation to body
            e.stopPropagation();
          }}
        >
          <div className="p-4 space-y-1">
            {/* Primary Navigation */}
            <div className="space-y-1 mb-4">
              {primaryItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground shadow-md nav-active'
                        : 'text-foreground hover:bg-primary/10 hover:text-primary border border-transparent hover:border-primary/20'
                    }`
                  }
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
            
            {/* Divider */}
            {secondaryItems.length > 0 && (
              <div className="border-t border-border my-3" />
            )}
            
            {/* Secondary Navigation */}
            <div className="space-y-1">
              {secondaryItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'bg-primary text-primary-foreground nav-active'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`
                  }
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate">{item.label}</span>
                </NavLink>
              ))}
            </div>
            
            {/* Bottom padding to ensure last items are scrollable above user section */}
            <div className="h-4" />
          </div>
        </nav>

        {/* Fixed User Section at Bottom */}
        <div className="flex-shrink-0 p-4 border-t bg-card">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-start gap-3 h-auto p-2 hover:bg-muted"
                data-testid="user-menu-trigger"
              >
                <Avatar className="h-9 w-9 flex-shrink-0">
                  <AvatarImage src={user?.avatarUrl} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <Badge variant="secondary" className="text-xs font-normal">
                    {user?.role}
                  </Badge>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  navigate('/profile');
                  setSidebarOpen(false);
                }}
                data-testid="profile-menu-item"
              >
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-destructive focus:text-destructive"
                data-testid="logout-menu-item"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pb-20 lg:pb-0">
        {/* Desktop Header */}
        <header className="hidden lg:flex glass-header sticky top-0 z-40 h-16 items-center justify-between px-6">
          <div />
          <div className="flex items-center gap-4">
            <NotificationBell />
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 sm:p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      {/* Daily Report Dialog */}
      <DailyReportDialog
        open={showDailyReport}
        onClose={() => setShowDailyReport(false)}
        onSubmit={handleDailyReportSubmit}
      />

      {/* Custom Scrollbar Styles */}
      <style jsx global>{`
        /* Custom scrollbar for sidebar */
        .scrollbar-thin::-webkit-scrollbar {
          width: 6px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-track {
          background: transparent;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb {
          background-color: rgba(156, 163, 175, 0.5);
          border-radius: 3px;
        }
        
        .scrollbar-thin::-webkit-scrollbar-thumb:hover {
          background-color: rgba(156, 163, 175, 0.7);
        }
        
        /* Hide scrollbar on mobile but keep scrolling */
        @media (max-width: 1024px) {
          .scrollbar-thin::-webkit-scrollbar {
            width: 4px;
          }
        }
        
        /* Smooth scrolling for the nav */
        .scrollbar-thin {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Firefox scrollbar */
        .scrollbar-thin {
          scrollbar-width: thin;
          scrollbar-color: rgba(156, 163, 175, 0.5) transparent;
        }
      `}</style>
    </div>
  );
};

export default DashboardLayout;
